import { i18n } from '@quadralize/i18n'
import { cn } from '@quadralize/utils'
import { CloseCircle } from '@solar-icons/react'
import type { ColumnDef, OnChangeFn, PaginationState } from '@tanstack/react-table'
import { type ReactNode, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useIntersectionTrigger } from '../../../hooks/use-intersection-trigger'
import { Button } from '../../ui/button/button'
import { AnimateIn, PageTransition, StaggerChildren } from '../../utils/motion-components'
import { DataTable } from './data-table'
import { DataTableActiveFilters } from './data-table-active-filters'
import { DataTableFilterSheet } from './data-table-filter-sheet'
import { DataTableFilters } from './data-table-filters'
import {
  type DataTableFilter,
  type DataTableSearchConfig,
  getActiveFilterCount,
} from './data-table-filters.types'
import { DataTableMobileFilters } from './data-table-mobile-filters'

interface DataTableViewProps<TData, TValue> {
  /** Optional className for root container */
  className?: string
  /** Table columns definition (desktop) */
  columns: ColumnDef<TData, TValue>[]
  /** Data array */
  data: TData[]
  /** Loading state */
  isLoading?: boolean

  /** Search configuration (optional - enables search on desktop and mobile) */
  search?: DataTableSearchConfig
  /** Filter configurations (optional - enables select filters on desktop and drawer on mobile) */
  filters?: DataTableFilter[]
  /** Callback to clear all filters (required when filters is provided) */
  onClearFilters?: () => void

  /** Mobile filter drawer configuration */
  filterDrawer?: {
    title?: string
    description?: string
  }

  /**
   * Desktop filter variant.
   * - 'inline' (default): renders filter selects in a bar above the table.
   * - 'sheet': renders a trigger button that opens a right-side filter sheet.
   */
  filterVariant?: 'inline' | 'sheet'

  /** Render function for mobile cards. If not provided, the table is shown on mobile too. */
  mobileCard?: (item: TData) => ReactNode
  /** Message when no results are found on mobile */
  mobileEmptyMessage?: string
  /** Custom empty state component to show when data is empty (desktop and mobile) */
  emptyState?: ReactNode

  // Pass-through DataTable props
  manualPagination?: boolean
  pageCount?: number
  rowCount?: number
  onPaginationChange?: OnChangeFn<PaginationState>
  pagination?: PaginationState
  onEndReached?: () => void
  isLoadingMore?: boolean
  showViewOptions?: boolean
  onRowClick?: (row: TData) => void
}

/**
 * Unified DataTable view that handles both desktop and mobile layouts.
 *
 * Desktop: search + filter selects + clear button + table
 * Mobile: search input + filter button (inline) + card list + filter drawer
 *
 * @example
 * ```tsx
 * <DataTableView
 *   columns={columns}
 *   data={employees}
 *   isLoading={isLoading}
 *   search={{
 *     value: search,
 *     placeholder: 'Buscar por nome...',
 *     onChange: handleSearchChange,
 *   }}
 *   filters={filterConfigs}
 *   onClearFilters={handleClearFilters}
 *   filterDrawer={{ title: 'Filtros', description: 'Filtre por status' }}
 *   mobileCard={(employee) => (
 *     <Card key={employee.id}>...</Card>
 *   )}
 * />
 * ```
 */
export function DataTableView<TData, TValue>({
  className,
  columns,
  data,
  isLoading = false,
  search,
  filters,
  onClearFilters,
  filterDrawer,
  filterVariant = 'inline',
  mobileCard,
  mobileEmptyMessage,
  emptyState,
  // DataTable pass-through
  manualPagination,
  pageCount,
  rowCount,
  onPaginationChange,
  pagination,
  onEndReached,
  isLoadingMore,
  showViewOptions,
  onRowClick,
}: DataTableViewProps<TData, TValue>) {
  const { t } = useTranslation(['ui', 'common'], { i18n })
  const hasFilters = !!search || (filters && filters.length > 0)
  const activeFilterCount = filters ? getActiveFilterCount(filters) : 0
  const hasActiveToolbarFilters = activeFilterCount > 0 || !!search?.value
  const hasMobileCard = !!mobileCard
  const isEmpty = !isLoading && data.length === 0
  const shouldShowCustomEmpty = isEmpty && !!emptyState
  const mobileObserverTarget = useRef<HTMLDivElement>(null)
  const resolvedMobileEmptyMessage =
    mobileEmptyMessage ?? t('dataTable.mobileEmptyMessage', { ns: 'ui' })

  useIntersectionTrigger({
    target: mobileObserverTarget,
    onIntersect: onEndReached,
    enabled: hasMobileCard && !!onEndReached,
    canTrigger: !isLoadingMore,
    threshold: 0.1,
  })

  return (
    <PageTransition className={cn('flex flex-col gap-3 md:h-full md:min-h-0 md:flex-1', className)}>
      {/* Desktop: Filters bar */}
      {hasFilters && filterVariant === 'inline' && (
        <AnimateIn variant="slideDown" className="hidden md:block">
          <DataTableFilters search={search} filters={filters} onClearFilters={onClearFilters} />
        </AnimateIn>
      )}

      {/* Desktop: Sheet variant — search + filter trigger button */}
      {hasFilters && filterVariant === 'sheet' && (
        <AnimateIn variant="slideDown" className="hidden md:block">
          <div className="flex min-w-0 items-center gap-2">
            <DataTableFilters
              className="min-w-0 flex-1"
              search={search}
              onClearFilters={undefined}
            />
            {filters && filters.length > 0 && onClearFilters && (
              <DataTableFilterSheet
                filters={filters}
                onClearFilters={onClearFilters}
                title={filterDrawer?.title}
                description={filterDrawer?.description}
                hasSearch={!!search?.value}
              />
            )}
            {hasActiveToolbarFilters && onClearFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onClearFilters}
                className="h-10 md:h-10 text-text-secondary hover:text-text-primary shrink-0"
                startIcon={<CloseCircle className="h-4 w-4" />}
              >
                {t('actions.clearFilters', { ns: 'common' })}
              </Button>
            )}
          </div>
        </AnimateIn>
      )}

      {/* Desktop: Active filter chips (only for sheet variant where filters are hidden) */}
      {filterVariant === 'sheet' && filters && filters.length > 0 && activeFilterCount > 0 && (
        <div className="hidden md:block">
          <DataTableActiveFilters filters={filters} />
        </div>
      )}

      {/* Desktop: Custom empty state or Table */}
      <AnimateIn
        variant="fadeIn"
        delay={0.1}
        className={cn(hasMobileCard ? 'hidden md:block' : '', 'md:min-h-0 md:flex-1')}
      >
        {shouldShowCustomEmpty ? (
          <div className="overflow-hidden rounded-md border border-border-subtle bg-bg-elevated md:flex md:h-full md:min-h-0 md:flex-1">
            <div className="flex min-h-75 flex-col md:h-full md:min-h-0 md:flex-1 md:*:mt-0! md:*:min-h-0">
              {emptyState}
            </div>
          </div>
        ) : (
          <DataTable
            className="md:min-h-0 md:flex-1"
            columns={columns}
            data={data}
            isLoading={isLoading}
            manualPagination={manualPagination}
            pageCount={pageCount}
            rowCount={rowCount}
            onPaginationChange={onPaginationChange}
            pagination={pagination}
            onEndReached={onEndReached}
            isLoadingMore={isLoadingMore}
            showViewOptions={showViewOptions}
            onRowClick={onRowClick}
          />
        )}
      </AnimateIn>

      {/* Mobile: Search + Filter trigger + Cards */}
      {hasMobileCard && (
        <div className="md:hidden pb-[calc(env(safe-area-inset-bottom)+7rem)]">
          <AnimateIn variant="slideDown">
            <DataTableMobileFilters
              search={search}
              filters={filters}
              onClearFilters={onClearFilters}
              filterDrawerTitle={filterDrawer?.title}
              filterDrawerDescription={filterDrawer?.description}
            />
          </AnimateIn>

          {filters && filters.length > 0 && (
            <DataTableActiveFilters filters={filters} className="mb-3" />
          )}

          {isLoading ? (
            <StaggerChildren className="flex flex-col gap-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <AnimateIn
                  key={`loading-mobile-${i.toString()}`}
                  variant="fadeIn"
                  className="h-32 w-full animate-pulse rounded-lg bg-bg-elevated"
                />
              ))}
            </StaggerChildren>
          ) : data.length === 0 ? (
            <AnimateIn variant="fadeIn">
              {emptyState ? (
                <div className="flex min-h-75 flex-col *:mt-0!">{emptyState}</div>
              ) : (
                <div className="flex h-32 w-full items-center justify-center rounded-lg border border-dashed border-border-default bg-bg-elevated p-4 text-center text-sm text-text-secondary">
                  {resolvedMobileEmptyMessage}
                </div>
              )}
            </AnimateIn>
          ) : (
            <StaggerChildren
              key={data.length} // Force re-animation on data change
              className="flex flex-col gap-4"
            >
              {data.map((item, index) => {
                // Try to extract id for better key, fallback to index
                const itemId =
                  typeof item === 'object' && item !== null && 'id' in item
                    ? String((item as Record<string, unknown>).id)
                    : String(index)
                return (
                  <AnimateIn key={itemId} variant="slideUp">
                    {mobileCard(item)}
                  </AnimateIn>
                )
              })}
            </StaggerChildren>
          )}
          {isLoadingMore ? (
            <StaggerChildren className="mt-4 flex flex-col gap-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <AnimateIn
                  key={`loading-mobile-more-${i.toString()}`}
                  variant="fadeIn"
                  className="h-32 w-full animate-pulse rounded-lg bg-bg-elevated"
                />
              ))}
            </StaggerChildren>
          ) : null}
          {onEndReached ? <div ref={mobileObserverTarget} className="h-4 w-full" /> : null}
        </div>
      )}
    </PageTransition>
  )
}
